// "Dispatch without weighbridge" — the counterpart to
// features/purchasing-intake/ReceiveManual.tsx on the sales side: counted
// goods (boxed firelighters, bagged/crated firewood) are COUNTED, never
// weighed, so DispatchWizard.tsx's mandatory "Weighbridge ticket" step forces
// the operator to fabricate gross/tare kg for something that was never on a
// weighbridge (fixed defect — see this goal's write-up). The backend already
// supports a ticket-less dispatch (`dispatchWriteSchema.ticketId` is
// nullable/optional, proven by the Howick e2e test's "Step 6"), so this is a
// second, simpler entry point alongside DispatchWizard rather than a mode
// inside it (same shape decision ReceiveManual.tsx documents for its own
// header comment). One page, no stepper: header (site/customer/optional sales
// order/dispatched-at/notes) + repeatable unit-aware lines
// (DispatchManualLinesEditor) + the same ledger-effect margin preview the
// wizard shows (DispatchMarginPreview) + Post, with the same one-click
// "Post & raise invoice" affordance for PER_LOAD customers the wizard offers.
//
// PARTIAL-FAILURE HANDLING (wave-gate BLOCKER fix): the composite mutation
// (useDispatchManual) throws TYPED errors so this screen never lies and never
// duplicates a document —
//   - DispatchManualPostError: the DRAFT exists but didn't post. The alert
//     says so truthfully, and the next Post RESUMES that draft
//     (`resumeDispatchId` -> PATCH + post) instead of creating a second one.
//   - DispatchManualRaiseError: the dispatch POSTED (stock/revenue effects are
//     real); only the invoice raise failed. The screen shows the POSTED panel
//     (from `error.post`) with an invoice-failure alert and a raise-only
//     retry — re-running the whole composite here would double-post.
//   - DispatchManualResumeError: a RESUME attempt's PATCH failed — usually
//     because the draft stopped being a draft (posted/reversed by another
//     operator between failure and retry). The screen fetches the dispatch's
//     CURRENT status and re-routes: POSTED -> the posted panel ("this draft
//     was already posted as DN-x"); still DRAFT -> keep it pinned and retry;
//     REVERSED/unknown -> UNPIN the draft so the next Post creates a fresh
//     dispatch. No path leaves `resumeDispatchId` pinned to an id the server
//     will never accept again (delta re-gate MAJOR fix, 2026-07-10).
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Group, Paper, Stack, Text, Textarea, TextInput, Title, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertCircle, IconCheck, IconTruck } from '@tabler/icons-react';
import type {
  CustomerPriceResponse,
  CustomerResponse,
  DispatchPostResponse,
  DispatchResponse,
  DispatchStatus,
  RaiseInvoicesResponse,
} from '@zebra/core';
import { apiFetch, ApiRequestError } from '../../api/client';
import { useCanOperate, useCanSell } from '../../auth/permissions';
import { CustomerSelect } from './components/CustomerSelect';
import { SalesOrderSelect } from './components/SalesOrderSelect';
import { SiteSelect } from './components/SiteSelect';
import { DownloadPdfLink } from './components/DownloadPdfLink';
import { DispatchMarginPreview, type MarginPreviewLine } from './components/DispatchMarginPreview';
import {
  DispatchManualLinesEditor,
  dispatchManualLinesValid,
  emptyDispatchManualLineRow,
  type DispatchManualLineFormRow,
} from './components/DispatchManualLinesEditor';
import { centsToRand, formatMoneyCents, formatQty, nowForDatetimeLocal, randToCents, tonneCentsToUnitRand } from './format';
import {
  DispatchManualPostError,
  DispatchManualRaiseError,
  DispatchManualResumeError,
  pickCurrentCustomerPrice,
  useAllProducts,
  useCustomer,
  useCustomerPrices,
  useDispatchManual,
  useRaiseInvoices,
} from './api';

/** The contract price for one product, as a Rand-string for the price input —
 * null when the customer has no current price for it (the field is then left
 * for the operator to type, mirroring DispatchWizard's prefill-only-when-known
 * behaviour). */
function contractPriceRand(prices: CustomerPriceResponse[] | undefined, productId: string): string | null {
  const current = pickCurrentCustomerPrice((prices ?? []).filter((p) => p.productId === productId));
  if (!current) return null;
  if (current.priceCentsPerUnit != null) return centsToRand(current.priceCentsPerUnit);
  if (current.priceCentsPerTonne != null) return tonneCentsToUnitRand(current.priceCentsPerTonne);
  return null;
}

/** What the screen learned after a resume-PATCH failure, driving the truthful
 * alert on the form view (the already-POSTED outcome routes to the posted
 * panel instead and never sets one of these). */
type ResumeNotice =
  | { kind: 'checking'; number: string }
  /** Still a DRAFT on the server — the PATCH failed for some other (likely
   * transient) reason; the draft stays pinned and Post retries it. */
  | { kind: 'retryable'; number: string; message: string }
  /** No longer resumable (REVERSED, or status could not be determined) — the
   * draft has been UNPINNED; the next Post creates a fresh dispatch. */
  | { kind: 'cleared'; number: string; status: DispatchStatus | null };

/** Rebuilds the posted panel's `DispatchPostResponse` shape from a by-id
 * `GET /sales/dispatches/:id` of an ALREADY-POSTED dispatch (a resume attempt
 * that discovered someone else had posted the draft first) — the by-id
 * response carries the posted lines' persisted money fields, so the panel's
 * figures are the real posted values, not estimates. `movementId` is not
 * recoverable from this shape (and the panel never renders it). */
function postResponseFromPostedDispatch(current: DispatchResponse, perLoad: boolean): DispatchPostResponse {
  return {
    dispatch: current,
    movements: current.lines
      .filter((l) => l.saleValueCents != null && l.costCents != null && l.marginCents != null)
      .map((l) => ({
        lineId: l.id,
        productId: l.productId,
        qty: l.qty,
        saleValueCents: String(l.saleValueCents),
        costCents: String(l.costCents),
        marginCents: String(l.marginCents),
        movementId: '',
      })),
    routing: { customerId: current.customerId, invoicingMode: perLoad ? 'PER_LOAD' : 'CONSOLIDATED', consolidationPeriod: null },
    deliveryNote: current.deliveryNoteKey ? { key: current.deliveryNoteKey, url: current.deliveryNoteUrl ?? undefined } : null,
    summary: `This draft was already posted as ${current.number} — likely by another user or an earlier attempt. No second dispatch was created.`,
  };
}

export function DispatchManual() {
  const canOperate = useCanOperate();
  const canSell = useCanSell();
  const canPostDispatch = canOperate || canSell;

  const [siteId, setSiteId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [salesOrderId, setSalesOrderId] = useState('');
  const [dispatchedAt, setDispatchedAt] = useState(nowForDatetimeLocal());
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DispatchManualLineFormRow[]>([emptyDispatchManualLineRow()]);
  /** DRAFT left behind by a failed post — the next Post resumes it. */
  const [resumeDraft, setResumeDraft] = useState<{ id: string; number: string } | null>(null);
  /** Outcome of a failed resume attempt (see ResumeNotice). */
  const [resumeNotice, setResumeNotice] = useState<ResumeNotice | null>(null);
  /** A resume attempt that discovered the draft ALREADY POSTED — rendered on
   * the same posted panel as a normal success. `perLoad` gates the
   * raise-invoice affordance (raising for a CONSOLIDATED customer without a
   * period would 400). */
  const [recovered, setRecovered] = useState<{ post: DispatchPostResponse; perLoad: boolean } | null>(null);
  /** Guards resolveResumeFailure against a stale async resolution landing
   * after the user has already started another attempt. */
  const attemptSeq = useRef(0);

  const customer = useCustomer(customerId || undefined);
  const customerPrices = useCustomerPrices({ customerId: customerId || undefined });
  const { data: products } = useAllProducts();
  const productsById = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, p])), [products]);
  const dispatchManual = useDispatchManual();
  const retryRaise = useRaiseInvoices();

  const isPerLoad = customer.data?.invoicingMode === 'PER_LOAD';

  // Prefill a line's price from the customer's current contract price the
  // moment a product is picked on a row whose price hasn't been hand-edited.
  // Detected by diffing each row's `productId` against its previous value,
  // since DispatchManualLinesEditor is a plain whole-array `onChange`.
  function handleLinesChange(newRows: DispatchManualLineFormRow[]) {
    const resolved = newRows.map((newRow) => {
      const oldRow = lines.find((r) => r.key === newRow.key);
      if (!customerId || !oldRow || oldRow.productId === newRow.productId || newRow.priceTouched || !newRow.productId) return newRow;
      const priceRand = contractPriceRand(customerPrices.data?.items, newRow.productId);
      return priceRand === null ? newRow : { ...newRow, priceRand };
    });
    setLines(resolved);
  }

  // Re-prefill untouched lines whenever the price book changes — most
  // importantly when the CUSTOMER is switched (the query refetches and this
  // re-resolves every line against the NEW customer's contract; wave-gate
  // MAJOR fix: prices used to stick to whichever customer was selected at
  // product-pick time). Mirrors DispatchWizard.tsx's reactive prefill effect
  // keyed on `customerPrices.data`, applied per line and respecting each
  // row's own `priceTouched`.
  useEffect(() => {
    if (!customerId) return;
    const items = customerPrices.data?.items;
    if (!items) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (!row.productId || row.priceTouched) return row;
        const priceRand = contractPriceRand(items, row.productId);
        if (priceRand === null || priceRand === row.priceRand) return row;
        changed = true;
        return { ...row, priceRand };
      });
      return changed ? next : prev;
    });
  }, [customerId, customerPrices.data]);

  const linesValid = dispatchManualLinesValid(lines, productsById);
  const canSubmit = Boolean(siteId) && Boolean(customerId) && dispatchedAt !== '' && linesValid;

  const previewLines: MarginPreviewLine[] = lines
    .filter((l) => l.productId && l.qty)
    .map((l) => ({ key: l.key, productId: l.productId, productLabel: l.productLabel, qty: l.qty, priceCentsPerUnit: randToCents(l.priceRand || '0') }));

  function resetForm() {
    setSiteId('');
    setCustomerId('');
    setSalesOrderId('');
    setDispatchedAt(nowForDatetimeLocal());
    setNotes('');
    setLines([emptyDispatchManualLineRow()]);
    setResumeDraft(null);
    setResumeNotice(null);
    setRecovered(null);
    attemptSeq.current += 1;
    dispatchManual.reset();
    retryRaise.reset();
  }

  /** A resume-PATCH failed — find out what the draft IS now and re-route
   * truthfully instead of looping the same doomed PATCH: POSTED -> posted
   * panel; still DRAFT -> keep pinned (retryable); REVERSED/undeterminable ->
   * UNPIN so the next Post creates a fresh dispatch. */
  async function resolveResumeFailure(err: DispatchManualResumeError, draft: { id: string; number: string }) {
    const seq = attemptSeq.current;
    setResumeNotice({ kind: 'checking', number: draft.number });
    try {
      const current = await apiFetch<DispatchResponse>(`/sales/dispatches/${err.dispatchId}`);
      if (seq !== attemptSeq.current) return; // a newer attempt superseded this resolution
      if (current.status === 'POSTED') {
        let perLoad = false;
        try {
          const cust = await apiFetch<CustomerResponse>(`/sales/customers/${current.customerId}`);
          perLoad = cust.invoicingMode === 'PER_LOAD';
        } catch {
          // Invoicing mode unknown — offer only the Raise-invoices link, never
          // an immediate raise that could 400 for a CONSOLIDATED customer.
        }
        if (seq !== attemptSeq.current) return;
        setRecovered({ post: postResponseFromPostedDispatch(current, perLoad), perLoad });
        setResumeDraft(null);
        setResumeNotice(null);
        return;
      }
      if (current.status === 'DRAFT') {
        // Still editable — the PATCH failed for some other (likely transient)
        // reason; keep the draft pinned so the next Post retries the SAME one.
        setResumeNotice({
          kind: 'retryable',
          number: draft.number,
          message: err.originalError instanceof ApiRequestError ? err.originalError.message : err.message,
        });
        return;
      }
      // REVERSED (or any future non-resumable state): unpin.
      setResumeDraft(null);
      setResumeNotice({ kind: 'cleared', number: draft.number, status: current.status });
    } catch {
      if (seq !== attemptSeq.current) return;
      // Can't even determine what the draft is now — unpin rather than loop.
      setResumeDraft(null);
      setResumeNotice({ kind: 'cleared', number: draft.number, status: null });
    }
  }

  function post(alsoRaiseInvoice: boolean) {
    attemptSeq.current += 1;
    setResumeNotice(null);
    const draft = resumeDraft;
    dispatchManual.mutate(
      {
        siteId,
        customerId,
        salesOrderId: salesOrderId || null,
        dispatchedAt,
        notes: notes.trim() || null,
        lines: lines.map((l, i) => ({
          lineNo: i + 1,
          productId: l.productId,
          fromLocationId: l.fromLocationId,
          qty: l.qty.trim(),
          priceCentsPerUnit: randToCents(l.priceRand),
          vatTreatment: l.vatTreatment,
        })),
        alsoRaiseInvoice,
        resumeDispatchId: draft?.id ?? null,
      },
      {
        onSuccess: () => setResumeDraft(null),
        onError: (err) => {
          if (err instanceof DispatchManualPostError) {
            setResumeDraft({ id: err.dispatchId, number: err.dispatchNumber });
            return;
          }
          if (err instanceof DispatchManualResumeError && draft) {
            void resolveResumeFailure(err, draft);
          }
        },
      },
    );
  }

  // The dispatch is POSTED in three situations: the whole composite
  // succeeded; only the trailing raise failed (DispatchManualRaiseError
  // carries the real post response); or a resume attempt discovered the draft
  // had ALREADY been posted by someone else (`recovered`). All three render
  // the posted panel — never a false "could not post".
  const raiseError = dispatchManual.error instanceof DispatchManualRaiseError ? dispatchManual.error : null;
  const posted: DispatchPostResponse | null = dispatchManual.data?.post ?? raiseError?.post ?? recovered?.post ?? null;
  const raised: RaiseInvoicesResponse | null = dispatchManual.data?.raise ?? retryRaise.data ?? null;

  if (posted) {
    return (
      <Paper withBorder radius="md" p="xl" maw={720}>
        <Stack gap="md" align="flex-start">
          <Badge color="brandGreen" size="lg" leftSection={<IconCheck size={14} />}>
            Posted
          </Badge>
          <Title order={2}>Dispatch posted</Title>
          <Text>{posted.summary}</Text>
          <Stack gap={4}>
            {posted.movements.map((line) => (
              <Text key={line.lineId} size="sm" c="dimmed">
                {formatQty(line.qty)} dispatched — sale value {formatMoneyCents(line.saleValueCents)}, margin{' '}
                {formatMoneyCents(line.marginCents)}.
              </Text>
            ))}
          </Stack>
          <DownloadPdfLink
            label="Download delivery note"
            pdfKey={posted.deliveryNote?.key ?? null}
            downloadPath={`/sales/dispatches/${posted.dispatch.id}/delivery-note`}
          />
          {raiseError && !raised && (
            <Alert color="amber" icon={<IconAlertCircle size={16} />} title="Invoice not raised">
              <Stack gap="xs">
                <Text size="sm">
                  Dispatch {posted.dispatch.number} was posted successfully, but raising the invoice failed:{' '}
                  {raiseError.originalError instanceof ApiRequestError ? raiseError.originalError.message : raiseError.message}. The
                  load is safe — it sits in the uninvoiced queue until an invoice is raised.
                </Text>
                {retryRaise.isError && (
                  <Text size="sm" c="red">
                    {retryRaise.error instanceof ApiRequestError ? retryRaise.error.message : 'Retry failed.'}
                  </Text>
                )}
                <Group gap="sm">
                  <Button
                    size="xs"
                    variant="light"
                    loading={retryRaise.isPending}
                    onClick={() => retryRaise.mutate({ customerIds: [posted.routing.customerId] })}
                  >
                    Retry raising invoice
                  </Button>
                  <Button size="xs" variant="subtle" component={Link} to="/sales-invoicing/raise">
                    Open Raise invoices
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
          {recovered && !raised && (
            <Alert color="amber" icon={<IconAlertCircle size={16} />} title="Invoice">
              <Stack gap="xs">
                <Text size="sm">
                  {recovered.perLoad
                    ? "If this load's invoice hasn't been raised yet, you can raise it now."
                    : 'This load joins the uninvoiced queue for the next consolidated run — check Raise invoices if unsure.'}
                </Text>
                {retryRaise.isError && (
                  <Text size="sm" c="red">
                    {retryRaise.error instanceof ApiRequestError ? retryRaise.error.message : 'Raising failed.'}
                  </Text>
                )}
                <Group gap="sm">
                  {recovered.perLoad && (
                    <Button
                      size="xs"
                      variant="light"
                      loading={retryRaise.isPending}
                      onClick={() => retryRaise.mutate({ customerIds: [posted.routing.customerId] })}
                    >
                      Raise invoice now
                    </Button>
                  )}
                  <Button size="xs" variant="subtle" component={Link} to="/sales-invoicing/raise">
                    Open Raise invoices
                  </Button>
                </Group>
              </Stack>
            </Alert>
          )}
          {raised && (
            <Alert color={raised.invoices.length > 0 ? 'green' : 'amber'} icon={<IconCheck size={16} />} title="Invoice">
              {raised.summary}
            </Alert>
          )}
          <Group>
            {/* A bare `Link` (not Mantine's `component={Link}` polymorphism) so
                TanStack Router's `params` type is inferred correctly against the
                target route — mirrors DispatchWizard.tsx's identical pattern. */}
            <Link to="/sales-invoicing/dispatches/$dispatchId" params={{ dispatchId: posted.dispatch.id }}>
              <Button component="span" variant="light">
                View dispatch
              </Button>
            </Link>
            <Button leftSection={<IconTruck size={16} />} onClick={resetForm}>
              Dispatch another
            </Button>
          </Group>
        </Stack>
      </Paper>
    );
  }

  const postError = dispatchManual.error instanceof DispatchManualPostError ? dispatchManual.error : null;
  const resumeError = dispatchManual.error instanceof DispatchManualResumeError ? dispatchManual.error : null;

  return (
    <Stack gap="lg" maw={900}>
      <Title order={2}>Dispatch without weighbridge</Title>
      <Text c="dimmed" size="sm">
        For counted goods — boxed firelighters, bagged or crated firewood — dispatched without a weighbridge ticket.
        Bulk and other weighed loads still go through "Dispatch a load".
      </Text>

      {postError && (
        <Alert color="amber" icon={<IconAlertCircle size={16} />} title="Draft saved — posting failed">
          Draft {postError.dispatchNumber} was created, but posting it failed:{' '}
          {postError.originalError instanceof ApiRequestError ? postError.originalError.message : postError.message}. Fix the
          problem and click Post again — the SAME draft will be posted, never a duplicate.
        </Alert>
      )}
      {resumeNotice?.kind === 'checking' && (
        <Alert color="amber" icon={<IconAlertCircle size={16} />} title="Draft could not be updated">
          Draft {resumeNotice.number} could not be updated — checking its current status…
        </Alert>
      )}
      {resumeNotice?.kind === 'retryable' && (
        <Alert color="amber" icon={<IconAlertCircle size={16} />} title="Draft not updated">
          Draft {resumeNotice.number} could not be updated: {resumeNotice.message}. It is still a draft — click Post again to
          retry the SAME draft.
        </Alert>
      )}
      {resumeNotice?.kind === 'cleared' && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Draft no longer usable">
          Draft {resumeNotice.number} can no longer be posted
          {resumeNotice.status ? ` — it is now ${resumeNotice.status}` : ' — its current status could not be determined'}. The
          next Post will create a NEW dispatch.
        </Alert>
      )}
      {dispatchManual.isError && !postError && !raiseError && !resumeError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not create the dispatch">
          {dispatchManual.error instanceof ApiRequestError ? dispatchManual.error.message : 'Failed to dispatch.'}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Stack gap="md">
          <Group grow>
            <SiteSelect label="Site" required value={siteId} onChange={setSiteId} />
            <CustomerSelect
              label="Customer"
              required
              value={customerId}
              onChange={(id) => {
                setCustomerId(id);
                setSalesOrderId('');
              }}
            />
          </Group>
          <SalesOrderSelect
            label="Sales order (optional)"
            description="Link this dispatch to an existing order so it counts against what the customer still has coming."
            customerId={customerId || undefined}
            value={salesOrderId}
            onChange={setSalesOrderId}
          />
          <TextInput
            label="Dispatched at"
            description="This drives which day's ledger and reports the dispatch falls under."
            type="datetime-local"
            required
            value={dispatchedAt}
            onChange={(e) => setDispatchedAt(e.currentTarget.value)}
          />
          <Textarea label="Notes (optional)" autosize minRows={1} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Lines
          </Text>
          <DispatchManualLinesEditor rows={lines} onChange={handleLinesChange} siteId={siteId || undefined} productsById={productsById} />
        </Stack>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <DispatchMarginPreview siteId={siteId || undefined} lines={previewLines} />
      </Paper>

      {customer.data && (
        <Text size="sm" c="dimmed">
          Invoicing: <strong>{customer.data.invoicingMode}</strong>
          {isPerLoad
            ? ' — this load can be invoiced immediately after posting.'
            : ' — this load joins the uninvoiced queue for the next consolidated run.'}
        </Text>
      )}

      <Group justify="flex-end">
        {isPerLoad && (
          <Tooltip
            label={
              canPostDispatch
                ? 'Posts this dispatch and immediately raises the invoice — stock leaves the ledger, the document locks, and an invoice is created.'
                : 'Requires Operations or Sales & Accounts role'
            }
          >
            <span>
              <Button
                size="lg"
                variant="light"
                disabled={!canPostDispatch || !canSubmit}
                loading={dispatchManual.isPending}
                onClick={() => post(true)}
              >
                Post &amp; raise invoice
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip
          label={
            canPostDispatch ? 'Posts this dispatch — stock leaves the ledger and the document locks.' : 'Requires Operations or Sales & Accounts role'
          }
        >
          <span>
            <Button size="lg" disabled={!canPostDispatch || !canSubmit} loading={dispatchManual.isPending} onClick={() => post(false)}>
              Post
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}
